import { DateTime } from 'luxon'
import { mapActions, mapGetters } from 'vuex'
import constants from '~/lib/constants'
import ckeditor from '~/lib/ckeditor'
import componentlocks from '~/lib/componentlocks'
import errors from '~/lib/errors'
import events from '~/lib/events'
import modals from '~/lib/modals'
import tabs from '~/lib/tabs'
import versions from '~/lib/versions'

export default {
  data () {
    return {
      overrideListFilterVisible: undefined,
      showExactRange: false,
      persistedState: {},
      origState: null,
      loading: false,
      loadingFailed: false,
      saving: false,
      saved: false
    }
  },
  mixins: [ckeditor, componentlocks, constants, errors, events, modals, tabs, versions],
  props: ['activeLayout', 'className', 'component', 'column', 'columns', 'columnIndex', 'components', 'columnWidthClasses', 'defaultListFilterVisible'],
  computed: {
    ...mapGetters(['timezone', 'allSettings', 'shopId', 'locale', 'version', 'user', 'userData', 'pos', 'clientid', 'clientName', 'tabid', 'apiKey', 'floatingReceipt', 'unsavedChanges', 'dateFormat', 'timeFormat', 'numberFormat']),
    settings () {
      return this.shopSettings(this.shopId)
    },
    listFilterVisible: {
      get () {
        if (this.defaultListFilterVisible) {
          return true
        }
        return this.overrideListFilterVisible !== undefined ? this.overrideListFilterVisible : false
      },
      set (value) {
        this.overrideListFilterVisible = value
      }
    },
    shopDeliveryMethods () {
      let ret = {}
      for (let shopIdLocale in this.allSettings) {
        const [shopId, locale] = shopIdLocale.split('-')
        if (locale === this.locale) {
          ret[shopId] = this.allSettings[shopIdLocale].sharedDeliverymethod
        }
      }
      return ret
    },
    shortLocale () {
      return this.locale ? this.locale.split('_')[0] : ''
    },
    showColumnHeader () {
      if (this.component && this.component.initData && this.component.initData.modal) {
        return false
      }
      return true
    },
    bodyClassName () {
      if (this.className === 'modal-content') {
        return 'modal-body'
      }
      return ''
    },
    isFloatingReceiptAvailable () {
      if (!this.floatingReceipt) {
        return false
      }
      return true
    },
    persistedStateKey () {
      if (!this.column || !this.component) {
        return false
      }
      if (this.column.identifier === 'details') {
        return this.shopId + '-' + 'details-' + this.component.type.toLowerCase()
      }
      return this.shopId + '-' + this.column.identifier + '-' + this.columnIndex + '-' + this.component.id
    },
    currState () {
      return this.getSaveDataString()
    },
    saveShouldClose () {
      return false
    },
    isSaveButtonEnabled () {
      return !this.saving && !this.saved && !this.loading && !this.locked && !this.waitingForUploads
    },
    isApproveButtonEnabled () {
      return this.isSaveButtonEnabled
    },
    isRemoveButtonEnabled () {
      return this.isSaveButtonEnabled
    },
    saveButtonIcon () {
      if (this.saving || this.waitingForUploads) {
        return 'cog fa-spin'
      }
      if (this.saved) {
        return 'check'
      }
      return 'save'
    },
    saveButtonText () {
      if (this.waitingForUploads) {
        return this._('Waiting for images...')
      }
      if (this.saving) {
        return this._('Saving...')
      }
      if (this.saved) {
        return this._('Saved!')
      }
      return this._('Save')
    },
    columnWidth () {
      if (!this.columns) {
        return 12
      }
      const widths = this.columns.map(c => c.width)
      const details = this.columns.find(c => c.identifier === 'details') // Details column's width should be deducted entirely from the first column's width
      const minus = details ? details.width : 0
      if (widths[this.columnIndex] && minus) {
        widths[this.columnIndex] -= minus
      }
      return widths[this.columnIndex] ? widths[this.columnIndex] : 12
    },
    columnClassName () {
      return this.className + ' ' + this.columnWidthClasses
    }
  },
  methods: {
    ...mapActions([
      'setUnsavedChanges'
    ]),
    async toggleMultiSelect () {
      this.tableOptions.selectable = !this.tableOptions.selectable
      await nextTick()
      this.EventBus.$emit('redrawDataTable')
    },
    persist (key, data, expiration) {
      if (!this.persistedState) {
        this.persistedState = {}
      }
      if (!this.persistedStateKey) {
        return
      }
      this.persistedState[key] = data
      if (expiration && expiration instanceof DateTime) {
        this.persistedState['_expiration'] = expiration.toUnixInteger()
      }
      localStorage.setItem(this.persistedStateKey, JSON.stringify(this.persistedState))
    },
    restorePersistedState () {
      if (!this.persistedStateKey) {
        return
      }
      try {
        this.persistedState = JSON.parse(localStorage.getItem(this.persistedStateKey))
        if (!this.persistedState) {
          this.persistedState = {}
        }
        if (this.persistedState['_expiration'] && DateTime.now().toUnixInteger() >= this.persistedState['_expiration']) {
          this.persistedState = {}
        } else {
          for (let key in this.persistedState) {
            if (Object.prototype.hasOwnProperty.call(this.persistedState, key)) {
              this[key] = this.persistedState[key]
            }
          }
        }
      } catch (e) {
        this.persistedState = {}
      }
    },
    shopSettings (shopId) {
      return this.allSettings[shopId + '-' + this.locale] || {}
    },
    setActiveComponent (component) {
      this.$emit('setActiveComponent', component)
    },
    setActiveLayout(id) {
      this.$emit('setActiveLayout', id);
    },
    remove (title) {
      if (!title || typeof title === 'object') { // Object: it's the mousedown event when called without ()
        title = this._('Are you sure? Deletion can\'t be undone.') 
      }
      this.showDialog({
        title,
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Delete'),
            class: 'danger',
            icon: 'trash',
            handler: () => {
              this.doRemove()
            }
          }
        ]
      })
    },
    close () {
      if (this.isModal) {
        this.$emit('close')
      } else {
        this.EventBus.$emit('hideDetailsColumn', {
          shopId: this.shopId,
          data: {
            trail: this.componentTrail
          }
        })
      }
    },
    getSaveData () {
      return {}
    },
    getSaveDataString () {
      const d = this.getSaveData()
      delete d.version
      return JSON.stringify(d)
    },
    storeState () {
      this.origState = this.getSaveDataString().replace(/(\\n|\\r)/g, '')
      this.setUnsavedChanges(false)
    },
    exactRangeDayString (date) {
      if (!(date instanceof Date)) {
        return null
      }
      return DateTime.fromJSDate(date, { zone: this.timezone }).toFormat('d.M.yyyy')
    },
    setExactRangeFromFilter (rangeValue) {
      const [startStr, endStr] = rangeValue.split('-')
      // Tabulator writes the active filter back through exact-range setters on
      // every dataFiltering event. Skip replacing Date objects when the shop
      // calendar days are unchanged, and always parse in the shop timezone so
      // a client in another zone does not shift the selected days.
      if (this.exactRangeDayString(this.dateStart) === startStr && this.exactRangeDayString(this.dateEnd) === endStr) {
        this.showExactRange = true
        return
      }
      this.dateStart = DateTime.fromFormat(startStr, 'd.M.yyyy', { zone: this.timezone }).toJSDate()
      this.dateEnd = DateTime.fromFormat(endStr, 'd.M.yyyy', { zone: this.timezone }).toJSDate()
      this.showExactRange = true
    },
    // The column root is the tab panel of its column header's tabs (JDEV-3207). Set here once for all column
    // components; the id comes from pages/[shopid]/index.vue or ColumnHeader.vue, and the active tab's id
    // follows ColumnTabs.vue (`${panelId}-tab-${component.id}`).
    applyTabpanelRole () {
      const el = this.$el
      if (!el || el.nodeType !== 1 || !this.showColumnHeader || !this.component || !/\bpos-column\b/.test(this.className || '')) {
        return
      }
      const header = el.querySelector('.header-navbar')
      if (!header || header.closest('.pos-column') !== el) {
        return
      }
      el.setAttribute('role', 'tabpanel')
      if (el.id && this.component.id != null) {
        el.setAttribute('aria-labelledby', el.id + '-tab-' + this.component.id)
      }
    },
    tableEvent (data) {
      if (data) {
        if (data.filters) {
          for (let i = 0; i < data.filters.length; i++) {
            let f = data.filters[i]
            switch (f.field) {
              case 'search':
                if (!this.search) { // We need to check this, otherwise the search field will start "eating" characters
                  this.search = f.value
                }
                break
              case 'type':
              case 'displayedtype':
                this.type = f.value
                break
              case 'category':
                this.category = f.value
                break
              case 'status':
                this.status = f.value
                break
              case 'admintag':
              case 'tag':
                this.tag = f.value
                break
              case 'pos_name':
                this.pos = f.value
                break
              case 'resourcepool':
                this.pool = f.value
                break
              case 'ts':
              case 'orderdate':
              case 'invoicedate':
                this.dateRangeFilter = f.value
                break
              case 'start_ts':
                this.startDateFilter = f.value
                break
              case 'end_ts':
                this.endDateFilter = f.value
                break
            }
          }
        }
      }
    }
  },
  watch: {
    columnWidth: {
      async handler (newValue) {
        await nextTick()
        this.EventBus.$emit('redrawDataTable')
      }
    },
    showExactRange: {
      async handler (newValue) {
        await nextTick()
        this.EventBus.$emit('redrawDataTable')
      }
    },
    'component.id' () {
      nextTick(() => this.applyTabpanelRole())
    },
    async version (newValue, oldValue) {
      if (!oldValue || !newValue || newValue === oldValue) {
        return
      }
      if (typeof this.reloadOnVersionChange !== 'function') {
        return
      }
      try {
        await this.reloadOnVersionChange()
      } catch (e) {}
    }
  },
  async mounted () {
    this.$emit('observe', this.$el)
    this.applyTabpanelRole()
    this.restorePersistedState()
    if (this.component && this.component.initData && this.component.initData.activeTab) {
      this.activeTab = this.component.initData.activeTab
    }
    if(this.component && this.component.type != 'Receipt') {
      this.getComponentLock()
    }
    window.addEventListener('beforeunload', this.releaseComponentLockWrapper)
  },
  beforeUnmount () {
    this.$emit('unobserve', this.$el)
    this.releaseComponentLock()
    window.removeEventListener('beforeunload', this.releaseComponentLockWrapper)
  }
}